/**
 * lib/validation.ts
 * ------------------------------------------------------------------
 * Field-level validators shared by the public Application form
 * (PublicApplicationForm) and the staff Admissions form
 * (AdmissionsModule). Both surfaces enforce the same rules so the
 * data quality is consistent regardless of who entered it.
 *
 * Phone rules (per LKC spec):
 *   - All numbers MUST include a country code (must start with '+').
 *   - Botswana mobile (country code +267):
 *       - first two digits after the country code are one of
 *         71, 72, 73, 74, 75, 76, 77, 78
 *       - total length after the country code is 8 digits
 *   - Botswana landline:
 *       - total length after the country code is 7 digits
 *       - first digit after the country code is 2, 3, 4, 5 or 6
 *         (these are the historical Botswana landline area codes)
 *   - Non-Botswana numbers:
 *       - must be valid E.164-ish: '+' then 8..15 digits, no
 *         spaces or other characters.
 *
 * Email rules:
 *   - HTML5-style sanity check (no exhaustive RFC 5322 implementation).
 *
 * All validators return `null` for valid input and a human-readable
 * error string for invalid input. This shape makes it easy to render
 * inline errors and to combine multiple validators on one field.
 * ------------------------------------------------------------------
 */
import type { Guardian } from '@/types';

export type ValidatorResult = string | null;

/* ---------------- Email ---------------- */

const EMAIL_REGEX = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

/**
 * Validate an email address. Returns null if valid, an error
 * message if not. Empty/undefined input returns null (treat as
 * "no value entered" — callers decide if it was required).
 */
export function validateEmail(input: string | undefined | null): ValidatorResult {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > 254) return 'Email address is too long.';
  if (!EMAIL_REGEX.test(trimmed)) {
    return 'Enter a valid email address (e.g. name@example.com).';
  }
  return null;
}

export function validateRequiredEmail(input: string | undefined | null): ValidatorResult {
  if (!input || String(input).trim().length === 0) return 'Email is required.';
  return validateEmail(input);
}

/* ---------------- Phone ---------------- */

const BW_MOBILE_PREFIXES = new Set(['71', '72', '73', '74', '75', '76', '77', '78']);
const BW_LANDLINE_AREA_CODES = new Set(['2', '3', '4', '5', '6']);
const BW_COUNTRY = '+267';

/** Strip every character except '+' (for leading) and digits. */
function normalisePhone(raw: string): string {
  const s = String(raw).trim();
  if (!s) return '';
  // Allow one leading +, strip all spaces, dashes, parens
  const cleaned = s.replace(/[\s().-]/g, '');
  return cleaned;
}

/**
 * Validate a Botswana mobile or landline number, or any E.164 number
 * with country code.
 *
 * Returns null if valid, error message if not. Empty/undefined input
 * returns null (treat as "no value entered" — callers decide if it
 * was required).
 */
export function validatePhone(input: string | undefined | null): ValidatorResult {
  if (!input) return null;
  const phone = normalisePhone(String(input));
  if (phone.length === 0) return null;

  if (!phone.startsWith('+')) {
    return 'Phone number must start with a country code (e.g. +267 for Botswana).';
  }

  // Strip leading + for the digit checks
  const digits = phone.slice(1);
  if (!/^\d+$/.test(digits)) {
    return 'Phone number must contain only digits after the country code.';
  }

  // Botswana-specific rules
  if (phone.startsWith(BW_COUNTRY)) {
    const local = digits.slice(3); // strip 267
    if (local.length === 8) {
      // Mobile — first two digits must be in the allowed set
      const prefix = local.slice(0, 2);
      if (!BW_MOBILE_PREFIXES.has(prefix)) {
        return (
          'Botswana mobile numbers must start with 71, 72, 73, 74, 75, ' +
          '76, 77 or 78 after the country code.'
        );
      }
      return null;
    }
    if (local.length === 7) {
      // Landline
      const area = local.slice(0, 1);
      if (!BW_LANDLINE_AREA_CODES.has(area)) {
        return 'Botswana landline numbers must start with 2, 3, 4, 5 or 6 after the country code.';
      }
      return null;
    }
    return (
      'Botswana numbers must be 8 digits (mobile) or 7 digits (landline) ' +
      'after the +267 country code.'
    );
  }

  // Non-Botswana: enforce E.164 length bounds
  if (digits.length < 8) return 'Phone number is too short for an international format.';
  if (digits.length > 15) return 'Phone number is too long for an international format.';
  return null;
}

export function validateRequiredPhone(input: string | undefined | null): ValidatorResult {
  if (!input || String(input).trim().length === 0) return 'Phone number is required.';
  return validatePhone(input);
}

/* ---------------- Date of birth ---------------- */

/**
 * Validate an ISO date string (YYYY-MM-DD). For an applicant, also
 * checks that the date is in the past, not absurdly old, and not
 * in the future. minAge / maxAge are optional — if supplied, the
 * validator also enforces that the resulting age is within range
 * on the reference date (default: today).
 */
export function validateDateOfBirth(
  input: string | undefined | null,
  opts: { minAge?: number; maxAge?: number; referenceDate?: Date } = {},
): ValidatorResult {
  if (!input) return null;
  const s = String(input).trim();
  if (s.length === 0) return null;

  // Must match YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return 'Date of birth must be in YYYY-MM-DD format.';
  }
  const dob = new Date(s + 'T00:00:00Z');
  if (Number.isNaN(dob.getTime())) return 'Date of birth is not a valid date.';

  const ref = opts.referenceDate ?? new Date();
  if (dob.getTime() > ref.getTime()) {
    return 'Date of birth cannot be in the future.';
  }

  // Loose bounds: nobody alive today was born before 1900
  const earliest = new Date('1900-01-01T00:00:00Z');
  if (dob.getTime() < earliest.getTime()) {
    return 'Date of birth is too far in the past.';
  }

  // Compute age in years on reference date
  const ageMs = ref.getTime() - dob.getTime();
  const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);

  if (typeof opts.minAge === 'number' && ageYears < opts.minAge) {
    return `Applicant must be at least ${opts.minAge} years old.`;
  }
  if (typeof opts.maxAge === 'number' && ageYears > opts.maxAge) {
    return `Applicant cannot be older than ${opts.maxAge} years.`;
  }
  return null;
}

/* ---------------- Name / required text ---------------- */

export function validateName(
  input: string | undefined | null,
  fieldLabel = 'Name',
): ValidatorResult {
  if (!input) return `${fieldLabel} is required.`;
  const s = String(input).trim();
  if (s.length === 0) return `${fieldLabel} is required.`;
  if (s.length < 2) return `${fieldLabel} must be at least 2 characters.`;
  if (s.length > 80) return `${fieldLabel} is too long.`;
  // Letters (incl. accents), spaces, hyphens, apostrophes only.
  if (!/^[\p{L}\s'\-.]+$/u.test(s)) {
    return `${fieldLabel} contains invalid characters.`;
  }
  return null;
}

/* ---------------- Occupation ---------------- */

/**
 * A guardian's occupation. REQUIRED on the two parent-facing forms — the
 * public application and the parent intake page — and left optional on the
 * staff admissions screen, where the person keying the record may simply not
 * have been told yet.
 *
 * Deliberately NOT validateName: an occupation is not a name. "Grade 2
 * Nurse", "IT Manager (Systems)", "Self-employed — Sechaba Motors" and
 * "Teacher / Farmer" are all ordinary answers, and every one of them fails a
 * letters-and-hyphens-only rule. So digits, slashes, brackets, commas,
 * ampersands and full stops are all allowed.
 *
 * What is rejected is a non-answer: nothing at all, or a single character, or
 * punctuation on its own. Somebody typing "-" to get past a required field
 * leaves the school with a record that looks complete and says nothing.
 * "Unemployed" and "Housewife" are real answers and pass.
 */
export function validateOccupation(
  input: string | undefined | null,
  fieldLabel = 'Occupation',
): ValidatorResult {
  const s = String(input ?? '').trim();
  if (s.length === 0) return `${fieldLabel} is required.`;
  if (s.length > 80) return `${fieldLabel} is too long.`;
  // At least two letters, so "-" and "." and "12" are not answers.
  if ((s.match(/\p{L}/gu) ?? []).length < 2) {
    return `Please give ${fieldLabel.toLowerCase()} in words.`;
  }
  if (!/^[\p{L}\p{N}\s'\-–—.,:/&+()]+$/u.test(s)) {
    return `${fieldLabel} contains invalid characters.`;
  }
  return null;
}

/* ---------------- ID number (Botswana Omang or passport) ---------------- */

/**
 * Validate a Botswana Omang (9 digits) OR a passport number. Loose
 * — only enforces some sanity bounds and an alphanumeric format,
 * since passport formats vary by country.
 */
export function validateIdNumber(input: string | undefined | null): ValidatorResult {
  if (!input) return null;
  const s = String(input).trim();
  if (s.length === 0) return null;
  if (s.length > 30) return 'ID / passport number is too long.';
  // Allow letters and digits and hyphens — covers passports too
  if (!/^[A-Za-z0-9\-]+$/.test(s)) {
    return 'ID / passport number must contain only letters, digits and hyphens.';
  }
  return null;
}

/* ---------------- Composite: validate a Guardian record ---------------- */

export interface GuardianValidationResult {
  /** Field-keyed map of error messages. Empty if valid. */
  errors: Partial<Record<keyof Guardian, string>>;
  /** True if and only if `errors` is empty. */
  ok: boolean;
}

/**
 * Validate a guardian record. Field-level rules:
 *   - fullName: required, name format
 *   - phone / whatsapp / email: optional but, if present, must be
 *     valid. The primary contact must have AT LEAST one reachable
 *     channel (phone, whatsapp, or email) — enforced at the
 *     guardians-list level, not here.
 *   - idNumber: optional, loose format
 */
export function validateGuardianFields(g: Guardian): GuardianValidationResult {
  const errors: Partial<Record<keyof Guardian, string>> = {};

  const nameErr = validateName(g.fullName, 'Guardian name');
  if (nameErr) errors.fullName = nameErr;

  const phoneErr = validatePhone(g.phone);
  if (phoneErr) errors.phone = phoneErr;

  const waErr = validatePhone(g.whatsapp);
  if (waErr) errors.whatsapp = waErr;

  const emailErr = validateEmail(g.email);
  if (emailErr) errors.email = emailErr;

  const idErr = validateIdNumber(g.idNumber);
  if (idErr) errors.idNumber = idErr;

  return { errors, ok: Object.keys(errors).length === 0 };
}
